/**
 * Mapbox `road` タイルから「start→end の直線近傍で歩いて渡れる橋」を抽出する純ロジック。
 *
 * 従来の橋認識は OSM Overpass (fetchWalkableBridgesNearSegment) だけに依存しており、
 * Overpass の間欠障害 (504/タイムアウト, 教訓42) 時は橋経由の最短候補が黙って消えていた
 * (実測: プライムアーバン日本橋茅場町の新亀島橋 650m 候補が Overpass 失敗時のみ出ない)。
 * Mapbox streets の `road` レイヤーは `structure=bridge` と `class` を持つため、
 * アプリが表示に使っているタイルから同等の橋列挙ができる。プローブでの取得は
 * services/mapbox-footprint.ts (fetchMapboxBridges)、ここは feature 配列 → 橋の純変換。
 *
 * 抽出仕様は Overpass 版 (services/overpass.ts fetchWalkableBridgesNearSegment) と同じ:
 * 橋ジオメトリの頂点のうち直線に最も近い点を横断点とし、直線から corridorMeters 以内・
 * 線分の範囲内 (frac -0.05..1.05) のものを近い順に maxBridges 本。
 */
import {
  lineString as turfLineString,
  point as turfPoint,
  nearestPointOnLine,
  length as turfLength,
} from '@turf/turf';
import type { BridgeCrossing } from '@/services/overpass';
import type { LngLat } from '@/types/property';
import { haversineMeters } from '@/lib/geo/distance';

/** querySourceFeatures('composite', {sourceLayer:'road'}) が返す feature の必要部分。 */
export interface TileRoadFeature {
  properties?: {
    structure?: string;
    class?: string;
    name?: string;
    name_ja?: string;
  } | null;
  geometry: {
    type: string;
    coordinates: unknown;
  };
}

/**
 * 歩いて渡れない road class (Mapbox streets-v8 の class 値)。
 * Overpass 版の isWalkableBridge (motorway/trunk 除外) と同義。
 */
const UNWALKABLE_CLASS =
  /^(motorway|motorway_link|trunk|trunk_link|construction|ferry)$/;

/** 同一橋の分割 feature (タイル境界・上下線) をまとめる横断点の距離 (m)。 */
const DEDUPE_METERS = 40;

/**
 * road feature 群から「線分 a→b を横切る歩行可能な橋」を抽出する。
 * 戻り値は Overpass 版と同型 (BridgeCrossing)。直線に近い順・最大 maxBridges 本。
 */
export function bridgesFromRoadFeatures(
  features: TileRoadFeature[],
  a: LngLat,
  b: LngLat,
  corridorMeters = 220,
  maxBridges = 4,
): BridgeCrossing[] {
  const seg = turfLineString([
    [a.lng, a.lat],
    [b.lng, b.lat],
  ]);
  const segLenKm = turfLength(seg, { units: 'kilometers' });

  const out: BridgeCrossing[] = [];
  for (const f of features) {
    const p = f.properties ?? {};
    if (p.structure !== 'bridge') continue;
    const cls = p.class ?? '';
    if (UNWALKABLE_CLASS.test(cls)) continue;

    // LineString / MultiLineString の全頂点から、直線に最も近い点を横断点にする
    const lines: Array<Array<[number, number]>> =
      f.geometry.type === 'LineString'
        ? [f.geometry.coordinates as Array<[number, number]>]
        : f.geometry.type === 'MultiLineString'
          ? (f.geometry.coordinates as Array<Array<[number, number]>>)
          : [];
    let best: { lng: number; lat: number; perp: number; loc: number } | null =
      null;
    for (const line of lines) {
      for (const [lng, lat] of line) {
        const np = nearestPointOnLine(seg, turfPoint([lng, lat]), {
          units: 'kilometers',
        });
        const perp = (np.properties.dist ?? Infinity) * 1000;
        const loc = np.properties.location ?? 0;
        if (!best || perp < best.perp) best = { lng, lat, perp, loc };
      }
    }
    if (!best) continue;
    const frac = segLenKm > 0 ? best.loc / segLenKm : 0.5;
    // 回廊の外・線分の外側 (端点より手前/先) は横断に無関係
    if (best.perp > corridorMeters || frac < -0.05 || frac > 1.05) continue;
    out.push({
      lng: best.lng,
      lat: best.lat,
      name: p.name_ja ?? p.name,
      highway: cls || 'unknown',
      perpMeters: Math.round(best.perp),
    });
  }

  // 同一橋の分割 feature を横断点の近接でまとめる (直線に近い方を残す)
  out.sort((x, y) => x.perpMeters - y.perpMeters);
  const deduped: BridgeCrossing[] = [];
  for (const c of out) {
    const dup = deduped.some(
      (d) =>
        haversineMeters({ lng: d.lng, lat: d.lat }, { lng: c.lng, lat: c.lat }) <
        DEDUPE_METERS,
    );
    if (!dup) deduped.push(c);
  }
  return deduped.slice(0, maxBridges);
}
